import React, { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import AdminSignupModal from "../../components/admin/AdminSignupModal"
import { useAdminSecretTap } from "../../context/AdminSecretTapContext"
import { useFeatureFlags } from "../../context/FeatureFlagsProvider"
import {
  clearAdminSession,
  setAuthSession,
  setDummyAdminSession,
  validateDummyCredentials,
} from "../../lib/adminAuth"
import { getChapterIntakeUploadPath, getPublicSiteOrigin, getAdminHomePath } from "../../lib/adminHost"
import apiClient from "../../lib/api"
import AdminAlert from "../../components/admin/AdminAlert"

const AdminLogin: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({ email: "", password: "" })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const handleSecretTap = useAdminSecretTap()
  const { flags } = useFeatureFlags()
  const [signupOpen, setSignupOpen] = useState(false)
  const autoLoginAttempted = useRef(false)

  const returnPath = searchParams.get("return") || getAdminHomePath()
  const isIntakeReturn = returnPath.includes("chapter-intake")
  const showSignup = flags.adminSignup && !isIntakeReturn && !claiming

  const finishLogin = (token: string, scope: "admin" | "chapter-intake", destination: string) => {
    setAuthSession(token, scope)
    navigate(destination)
  }

  const handleIntakeClaim = async (claimToken: string) => {
    setClaiming(true)
    setError(null)
    clearAdminSession()

    try {
      const response = await apiClient.claimChapterIntakeAccess(claimToken)
      finishLogin(response.token, "chapter-intake", response.uploadUrl || getChapterIntakeUploadPath())
    } catch {
      setError("This upload link has expired or is invalid. Contact the site team.")
    } finally {
      setClaiming(false)
    }
  }

  const handleCredentialLogin = async (email: string, password: string, destination: string) => {
    setIsSubmitting(true)
    setError(null)
    clearAdminSession()

    try {
      const response = await apiClient.login(email, password)
      const scope = response.scope === "chapter-intake" ? "chapter-intake" : "admin"
      const nextPath = scope === "chapter-intake" ? getChapterIntakeUploadPath() : destination
      finishLogin(response.token, scope, nextPath)
    } catch {
      if (validateDummyCredentials(email, password)) {
        setDummyAdminSession()
        navigate(getAdminHomePath())
        return
      }

      setError(isIntakeReturn ? "Invalid upload credentials." : "Invalid credentials.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email || !formData.password) {
      setError("Please fill in all fields")
      return
    }

    await handleCredentialLogin(formData.email, formData.password, returnPath)
  }

  // Run once on mount: claim magic link or auto-login from email+pass query params.
   
  useEffect(() => {
    const intakeToken = searchParams.get("intake")
    if (intakeToken) {
      void handleIntakeClaim(intakeToken)
      return
    }

    const email = searchParams.get("email")
    const pass = searchParams.get("pass")
    if (email) setFormData((prev) => ({ ...prev, email }))
    if (pass) setFormData((prev) => ({ ...prev, password: pass }))

    if (email && pass && !autoLoginAttempted.current) {
      autoLoginAttempted.current = true
      void handleCredentialLogin(email, pass, returnPath)
    }
  }, [searchParams])

  useEffect(() => {
    if (!showSignup) setSignupOpen(false)
  }, [showSignup])

  const busy = isSubmitting || claiming

  return (
    <div className="flex min-h-screen flex-col bg-[#f4f6fb]">
      <header className="border-b border-[#e2e8f0] bg-white px-6 py-4">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/dominion-city-logo.png" alt="" className="h-9 w-9 object-contain" />
            <div>
              <p className="font-outfit text-sm font-semibold uppercase tracking-wide text-[#00205c] cursor-default select-none">
                Dominion City
              </p>
              <p
                className="font-dm-sans text-xs uppercase tracking-[0.16em] text-[#9e7e43] cursor-default select-none"
                onClick={handleSecretTap}
              >
                Admin CMS
              </p>
            </div>
          </div>
          <a
            href={getPublicSiteOrigin()}
            className="font-dm-sans text-sm text-[#5a5a5a] transition hover:text-[#00205c]"
          >
            ← Back to website
          </a>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-2xl border border-[#e2e8f0] bg-white p-8 shadow-[0_20px_50px_-24px_rgba(0,32,92,0.25)]">
          <div className="mb-8 text-center">
            <h1 className="font-outfit text-2xl font-semibold tracking-tight text-[#1f1f1f]">
              {claiming ? "Opening upload session…" : isIntakeReturn ? "Sign in to upload" : "Sign in"}
            </h1>
            <p className="mt-2 font-dm-sans text-sm text-[#5a5a5a]">
              {isIntakeReturn
                ? "Use the upload email and passkey from your confirmation email. Access expires after 48 hours."
                : "Manage site content — events, locations, messages, and uploads."}
            </p>
          </div>

          {claiming ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-lg text-[#00205c]" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="flex flex-col gap-1.5">
                <span className="font-dm-sans text-sm font-medium text-[#2e2e2e]">Email</span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder={isIntakeReturn ? "chapter-dublin-…@intake.dominioncity.ie" : "Enter email"}
                  className="rounded-xl border border-[#e2e8f0] bg-[#fafafa] px-3.5 py-3 font-dm-sans text-base outline-none transition focus:border-[#00205c]/40 focus:ring-2 focus:ring-[#00205c]/10"
                  required
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="font-dm-sans text-sm font-medium text-[#2e2e2e]">Passkey</span>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter passkey"
                  className="rounded-xl border border-[#e2e8f0] bg-[#fafafa] px-3.5 py-3 font-dm-sans text-base outline-none transition focus:border-[#00205c]/40 focus:ring-2 focus:ring-[#00205c]/10"
                  required
                />
              </label>

              {error && <AdminAlert variant="error" message={error} onDismiss={() => setError(null)} />}

              <button
                type="submit"
                disabled={busy}
                className="mt-2 w-full rounded-xl bg-[#00205c] px-4 py-3 font-dm-sans text-sm font-semibold text-white transition hover:bg-[#003699] disabled:opacity-60"
              >
                {busy ? "Signing in…" : isIntakeReturn ? "Continue to upload" : "Sign in to Admin"}
              </button>

              {showSignup && (
                <button
                  type="button"
                  onClick={() => setSignupOpen(true)}
                  className="w-full rounded-xl border border-[#e2e8f0] px-4 py-3 font-dm-sans text-sm font-semibold text-[#00205c] transition hover:bg-[#f4f6fb]"
                >
                  Sign up
                </button>
              )}
            </form>
          )}
        </div>
      </main>

      <AdminSignupModal open={signupOpen} onClose={() => setSignupOpen(false)} />
    </div>
  )
}

export default AdminLogin
